"use client"

import * as React from "react"
import { Upload } from "lucide-react"
import { Button } from "~/components/ui/button"
import { cn } from "~/lib/utils"

/**
 * #1421 — the browser's own "Choose Files / no files selected" control has
 * no click affordance and keeps saying "no files selected" after a
 * successful pick (the picked files show in a list elsewhere, driven by the
 * caller's own state). This hides the native input and drives the button
 * and the "N files attached" text entirely from state, same pattern already
 * used on hosting/profile/edit — just factored out so every file-attach
 * surface gets it once.
 */

export function attachedFilesLabel(count: number, singular = "file", plural = `${singular}s`): string | null {
  if (count <= 0) return null
  return `${count} ${count === 1 ? singular : plural} attached`
}

export type FileAttachFieldProps = Omit<React.ComponentProps<"input">, "type" | "onChange" | "className"> & {
  /** Called with the picked files; the caller owns validation and state. */
  onFilesSelected: (files: File[]) => void
  /** Text shown next to the button — pass `attachedFilesLabel(n)` or a filename, null when nothing is attached. */
  attachedText?: string | null
  buttonText?: string
  buttonClassName?: string
  wrapperClassName?: string
}

export const FileAttachField = React.forwardRef<HTMLInputElement, FileAttachFieldProps>(
  (
    { onFilesSelected, attachedText = null, buttonText = "Choose files", buttonClassName, wrapperClassName, disabled, ...inputProps },
    ref,
  ) => {
    return (
      <div className={cn("flex flex-wrap items-center gap-3", wrapperClassName)}>
        <label
          className={cn(
            "cursor-pointer rounded-md focus-within:ring-[3px] focus-within:ring-ring/50",
            disabled && "pointer-events-none",
          )}
        >
          <input
            ref={ref}
            type="file"
            disabled={disabled}
            className="sr-only"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? [])
              if (files.length > 0) onFilesSelected(files)
              // Reset so picking the same file again still fires onChange.
              e.target.value = ""
            }}
            {...inputProps}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            asChild
            // `asChild` renders this as the <span> below, which has no CSS
            // `:disabled` pseudo-class — `disabled:opacity-50` etc. in
            // buttonVariants never applies, so the dimmed look needs an
            // explicit class here too (the real input is still disabled).
            className={cn(disabled && "opacity-50", buttonClassName)}
          >
            <span>
              <Upload className="h-4 w-4 mr-2 inline" />
              {buttonText}
            </span>
          </Button>
        </label>
        {attachedText && (
          <span className="text-sm text-[#5E594F] truncate">{attachedText}</span>
        )}
      </div>
    )
  },
)
FileAttachField.displayName = "FileAttachField"
